import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import caseStudiesLarge from '../assets/images/cms-case-studies-1536.webp';
import caseStudiesSmall from '../assets/images/cms-case-studies-768.webp';
import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import { caseStudies, caseStudyPath } from '../data/CaseStudies';

function CaseStudies() {
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 } }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          alignItems: 'center',
          gap: { xs: 3, md: 5 },
          mb: 5,
        }}
      >
        <Box>
          <Typography
            component='h1'
            variant='h3'
            sx={{ fontWeight: 800, color: '#0B2545', fontSize: { xs: '2rem', md: '3rem' } }}
          >
            CMS work in practice
          </Typography>
          <Typography
            variant='body1'
            sx={{ mt: 2, color: '#475569' }}
          >
            These delivered projects show how publishing tools served different teams. Each example
            identifies the work and its attribution.
          </Typography>
        </Box>
        <Box
          component='figure'
          aria-label='CMS work illustration'
          sx={{ m: 0, overflow: 'hidden', borderRadius: 3, bgcolor: '#F8FAFC' }}
        >
          <img
            src={caseStudiesLarge}
            srcSet={`${caseStudiesSmall} 768w, ${caseStudiesLarge} 1536w`}
            sizes='(max-width: 900px) calc(100vw - 48px), 540px'
            width={1536}
            height={1024}
            loading='eager'
            decoding='async'
            alt='Three publishing patterns: a shared site network, a venue calendar, and staged review.'
            style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
          />
        </Box>
      </Box>

      <Box
        component='nav'
        aria-label='Case study index'
        sx={{ mb: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
          sx={{ mb: 1, fontWeight: 700, color: '#0B2545' }}
        >
          Explore the case studies
        </Typography>
        <Box
          component='ul'
          sx={{ m: 0, pl: 3, display: 'grid', gap: 1 }}
        >
          {caseStudies.map((study) => (
            <li key={study.id}>
              <MuiLink
                component={Link}
                to={caseStudyPath(study.id)}
              >
                {study.title}
              </MuiLink>
            </li>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3 }}>
        {caseStudies.map((study) => (
          <Box
            component='article'
            key={study.id}
            id={study.id}
            tabIndex={-1}
            aria-labelledby={`${study.id}-title`}
            sx={{
              border: '1px solid rgba(11, 37, 69, 0.14)',
              borderRadius: 3,
              scrollMarginTop: '2rem',
              minWidth: 0,
              p: { xs: 3, md: 5 },
              bgcolor: '#FFFFFF',
            }}
          >
            <Typography
              variant='overline'
              sx={{ color: '#005596', fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {study.attribution}
            </Typography>
            <Typography
              component='h2'
              id={`${study.id}-title`}
              variant='h4'
              aria-label={`${study.title} — ${study.attribution}`}
              sx={{ mt: 1, mb: 3, fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {study.title}
            </Typography>
            <Box
              component='dl'
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: { xs: 2, md: 4 },
              }}
            >
              {(
                [
                  ['The need', study.problem],
                  ['What changed', study.result],
                ] as const
              ).map(([label, value]) => (
                <Box
                  key={label}
                  sx={
                    label === 'What changed'
                      ? {
                          p: 2,
                          borderRadius: 2,
                          bgcolor: '#EFF6FC',
                          borderLeft: '4px solid #005596',
                        }
                      : undefined
                  }
                >
                  <Typography
                    component='dt'
                    variant='subtitle1'
                    sx={{
                      fontWeight: 700,
                      color: label === 'What changed' ? '#0B2545' : undefined,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component='dd'
                    variant='body1'
                    sx={{ m: 0, color: label === 'What changed' ? '#0B2545' : '#475569' }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <MuiLink
              component={Link}
              to={caseStudyPath(study.id)}
              aria-label={`Read the case study: ${study.title}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 3,
                fontWeight: 700,
              }}
            >
              Read the case study
              <ArrowForwardIcon fontSize='small' />
            </MuiLink>
          </Box>
        ))}
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default CaseStudies;
